import React, { useCallback, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import ThemedSelect from '../shared/ThemedSelect';

const DELETE_TIMEFRAMES = [
  { id: 'all', label: 'All trades' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'ytd', label: 'Year to date' },
];

async function parseJsonResponse(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { error: text || 'Request failed' };
  }
}

/**
 * Superuser modal: delete trade history by timeframe.
 */
export default function PerformanceTradeSyncModal({
  show,
  onHide,
  accountId,
  isPaperTrading,
  authToken,
  backendUrl,
  showMessage,
  onSuccess,
}) {
  const { colors } = useTheme();

  const [deleteTimeframe, setDeleteTimeframe] = useState('all');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleDelete = useCallback(async () => {
    if (!accountId || deleteLoading) return;

    const label =
      DELETE_TIMEFRAMES.find((t) => t.id === deleteTimeframe)?.label || deleteTimeframe;
    const confirmed = window.confirm(
      `Delete ${label.toLowerCase()} for account ${accountId}? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeleteLoading(true);
    setLastResult(null);

    try {
      const qs = new URLSearchParams({ accountId });
      if (deleteTimeframe !== 'all') qs.set('timeframe', deleteTimeframe);

      const res = await fetch(`${backendUrl}/performance/trades?${qs.toString()}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const result = await parseJsonResponse(res);
      if (!res.ok) {
        showMessage?.(result.error || 'Delete failed', 'danger');
        return;
      }

      setLastResult(result);
      showMessage?.(
        `Deleted ${result.deletedTrades ?? 0} trades and ${result.deletedEvents ?? 0} events`,
        'success'
      );
      onSuccess?.();
    } catch (err) {
      showMessage?.(err?.message || 'Delete failed', 'danger');
    } finally {
      setDeleteLoading(false);
    }
  }, [accountId, authToken, backendUrl, deleteLoading, deleteTimeframe, onSuccess, showMessage]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="md"
      className="trade-risk-quick-setup-modal performance-trade-sync-modal"
      dialogClassName="trade-risk-quick-setup-dialog"
      contentClassName="trade-risk-quick-setup-content"
    >
      <Modal.Header closeButton style={{ borderColor: colors.border }}>
        <Modal.Title style={{ color: colors.text, fontSize: '1.1rem' }}>
          Trade History Settings
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ color: colors.text }}>
        <p style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '20px' }}>
          Permanently delete trade history for this account. This cannot be undone.
        </p>

        <div>
          <div
            style={{
              fontSize: '0.8rem',
              fontWeight: 600,
              marginBottom: '8px',
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Delete trades
          </div>
          <ThemedSelect
            value={deleteTimeframe}
            options={DELETE_TIMEFRAMES.map((t) => ({ value: t.id, label: t.label }))}
            onChange={setDeleteTimeframe}
            width="100%"
            minWidth={200}
          />
          <Button
            variant="outline-danger"
            disabled={deleteLoading || !accountId}
            onClick={handleDelete}
            style={{ marginTop: '12px', color: colors.chartDown, borderColor: colors.chartDown }}
          >
            {deleteLoading ? 'Deleting…' : 'Delete trades'}
          </Button>
        </div>

        {lastResult && (
          <div
            style={{
              marginTop: '20px',
              padding: '14px',
              borderRadius: '8px',
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '10px',
              }}
            >
              Result
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { label: 'Trades deleted', value: lastResult.deletedTrades ?? 0, accent: colors.chartDown },
                { label: 'Events deleted', value: lastResult.deletedEvents ?? 0, accent: undefined },
              ].map(({ label, value, accent }) => (
                <div
                  key={label}
                  style={{
                    flex: 1,
                    minWidth: 80,
                    padding: '10px 12px',
                    borderRadius: '6px',
                    backgroundColor: colors.bg,
                    border: `1px solid ${accent ? accent + '55' : colors.border}`,
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: accent || colors.text, lineHeight: 1.2 }}>
                    {value}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginTop: '3px' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderColor: colors.border }}>
        <Button
          variant="outline-light"
          onClick={onHide}
          style={{ borderColor: colors.border, color: colors.text, backgroundColor: 'transparent' }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
